import React, { useState, useMemo, useCallback } from "react"
import { BsTrash3Fill } from "react-icons/bs"
import { RiExpandDiagonalFill } from "react-icons/ri"
import Modal from "./Modal"
import { useEffect } from "react"
import { PageLoader } from "../Authentication/page-loader"
const ChairTable = ({ hasWriteSettingsPermission, chairs, setChairs, setChairsModified }) => {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [initialChairData, setInitialChairData] = useState(chairs)

	useEffect(() => {
		if (chairs && chairs.length > 0 && initialChairData.length === 0) {
			setInitialChairData(chairs);
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chairs]);

	const hasChairsDataChanged = useCallback(() => {
		if (initialChairData.length === 0) {
			setInitialChairData(chairs)
		}
		return JSON.stringify(chairs) !== JSON.stringify(initialChairData)
	}, [chairs, initialChairData])

	const openModal = () => setIsModalOpen(true)
	const closeModal = () => setIsModalOpen(false)

	const filteredChairs = useMemo(() => {
		if (!searchQuery.trim()) return chairs
		return chairs.filter(chair => chair.name.toLowerCase().includes(searchQuery.toLowerCase()))
	}, [chairs, searchQuery])

	const handleChairDelete = useCallback(
		index => {
			setChairs(prev => {
				const updated = [...prev]
				updated[index].name = null
				updated[index].userRemoved = true
				return updated
			})
			setChairsModified(true)
		},
		[setChairs, setChairsModified]
	)

	const getTypeFromDict = typeDict => {
		const assignedType = Object.keys(typeDict).find(key => typeDict[key])
		return assignedType ? assignedType : "Unassigned"
	}

	const getIsolationFromDict = isolationDict => {
		return Object.keys(isolationDict).find(key => isolationDict[key])
	}

	const totalChairs = filteredChairs && filteredChairs.length
	const totalActiveChairs =
		filteredChairs && filteredChairs.filter(chair => chair.status === true).length
	const totalInactiveChairs =
		filteredChairs && filteredChairs.filter(chair => chair.status === false).length
	const chairTypeCounts =
		filteredChairs &&
		filteredChairs.reduce((acc, chair) => {
			const type = getTypeFromDict(chair.type)
			acc[type] = (acc[type] || 0) + 1
			return acc
		}, {})

	if (!chairs || chairs.length === 0) {
		return <PageLoader />
	}

	return (
		<div className="mt-10">
			<div className="flex justify-between items-center mb-4">
				<h2 className="text-2xl font-semibold text-gray-900">Chairs</h2>
				<button
					type="button"
					onClick={openModal}
					className="flex items-center justify-center p-2 bg-customBlue text-white rounded-md hover:bg-customBlue-dark focus:outline-none shadow-md transition duration-200"
					aria-label="Expand"
				>
					<RiExpandDiagonalFill size={20} />
				</button>
			</div>

			<p className="text-sm text-gray-500 mt-2">
				{`Total: ${totalChairs}, Active: ${totalActiveChairs}, Inactive: ${totalInactiveChairs}, `}
				{Object.entries(chairTypeCounts).map(([type, count]) => (
					<span key={type}>
						{`${type.charAt(0).toUpperCase() + type.slice(1)}s: ${count}`}
						{", "}
					</span>
				))}
			</p>

			<div className="overflow-x-auto">
				<div className="max-h-96 overflow-y-auto">
					<div className="mb-4">
						<input
							type="text"
							placeholder="Search chairs..."
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
						/>
					</div>

					<table className="min-w-full divide-y divide-gray-200 mt-4 table-auto">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Chair Name
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Type
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Isolation Status
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Status
								</th>
								{hasWriteSettingsPermission && (
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Actions
									</th>
								)}
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{filteredChairs.map(chair => {
								// Find the index in the original chairs array using the chair's ID
								const originalIndex = chairs.findIndex(c => c.id === chair.id)
								return (
									<tr key={chair.id}>
										<td className="px-6 py-6 align-top text-sm text-gray-900">
											{hasWriteSettingsPermission ? (
												<input
													type="text"
													value={chair.name}
													onChange={e => {
														const updatedChairs = [...chairs]
														updatedChairs[originalIndex].name =
															e.target.value
														setChairs(updatedChairs)
														setChairsModified(hasChairsDataChanged())
													}}
													className="block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
													placeholder="Chair Name"
												/>
											) : (
												<span>{chair.name}</span>
											)}
										</td>
										<td className="px-6 py-6 align-top text-sm text-gray-900">
											{hasWriteSettingsPermission ? (
												<select
													value={getTypeFromDict(chair.type)}
													onChange={e => {
														const updatedChairs = [...chairs]
														const newType = {
															Chair: false,
															Bed: false,
															Gurney: false,
															[e.target.value]: true
														}
														updatedChairs[originalIndex].type = newType
														setChairs(updatedChairs)
														setChairsModified(
															hasChairsDataChanged()
														)
													}}
													className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												>
													<option value="Chair">Chair</option>
													<option value="Bed">Bed</option>
													<option value="Gurney">Gurney</option>
												</select>
											) : (
												<span>{getTypeFromDict(chair.type)}</span>
											)}
										</td>
										<td className="px-6 py-6 align-top text-sm text-gray-900">
											{hasWriteSettingsPermission ? (
												<select
													value={getIsolationFromDict(chair.isolation)}
													onChange={e => {
														const updatedChairs = [...chairs]
														const newIsolation = {
															"Public room": false,
															"Private room with bathroom": false,
															"Private room without bathroom": false,
															[e.target.value]: true
														}
														updatedChairs[originalIndex].isolation =
															newIsolation
														setChairs(updatedChairs)
														setChairsModified(
															hasChairsDataChanged()
														)
													}}
													className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												>
													<option value="Public room">Public room</option>
													<option value="Private room with bathroom">
														Private room with bathroom
													</option>
													<option value="Private room without bathroom">
														Private room without bathroom
													</option>
												</select>
											) : (
												<span>{getIsolationFromDict(chair.isolation)}</span>
											)}
										</td>
										<td className="px-6 py-6 align-top text-sm text-gray-900">
											{hasWriteSettingsPermission ? (
												<select
													value={chair.status ? "true" : "false"}
													onChange={e => {
														const updatedChairs = [...chairs]
														const newStatus = e.target.value === "true"
														updatedChairs[originalIndex].status =
															newStatus
														setChairs(updatedChairs)
														setChairsModified(
															hasChairsDataChanged()
														)
													}}
													className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												>
													<option value="true">Active</option>
													<option value="false">Inactive</option>
												</select>
											) : (
												<span>{chair.status ? "Active" : "Inactive"}</span>
											)}
										</td>
										{hasWriteSettingsPermission && (
											<td className="px-6 py-6 align-top text-sm text-gray-900">
												<button
													type="button"
													onClick={() => handleChairDelete(originalIndex)}
													className="py-2 px-4 w-24 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-2"
												>
													<BsTrash3Fill className="h-4 w-4" />
													Delete
												</button>
											</td>
										)}
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>

			<Modal isOpen={isModalOpen} onClose={closeModal}>
				<div className="overflow-x-auto max-h-screen">
					<div className="flex justify-between items-center mb-4">
						<h2 className="text-2xl font-semibold text-gray-900">Chairs</h2>
					</div>
					<p className="text-sm text-gray-500 mt-2">
						{`Total: ${totalChairs}, Active: ${totalActiveChairs}, Inactive: ${totalInactiveChairs}, `}
						{Object.entries(chairTypeCounts).map(([type, count]) => (
							<span key={type}>
								{`${type.charAt(0).toUpperCase() + type.slice(1)}s: ${count}`}
								{", "}
							</span>
						))}
					</p>

					<div className="mb-4">
						<input
							type="text"
							placeholder="Search chairs..."
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
						/>
					</div>

					<div className="overflow-x-auto">
						<div className="overflow-y-auto">
							<table className="min-w-full divide-y divide-gray-200 table-auto">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Chair Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Type
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Isolation Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										{hasWriteSettingsPermission && (
											<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Actions
											</th>
										)}
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{filteredChairs.map(chair => {
										// Find the index in the original chairs array using the chair's ID
										const originalIndex = chairs.findIndex(
											c => c.id === chair.id
										)
										return (
											<tr key={chair.id}>
												<td className="px-6 py-6 align-top text-sm text-gray-900">
													{hasWriteSettingsPermission ? (
														<input
															type="text"
															value={chair.name}
															onChange={e => {
																const updatedChairs = [...chairs]
																const newValue = e.target.value
																updatedChairs[originalIndex].name =
																	newValue
																setChairs(updatedChairs)
																setChairsModified(
																	hasChairsDataChanged()
																)
															}}
															className="block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
															placeholder="Chair Name"
														/>
													) : (
														<span>{chair.name}</span>
													)}
												</td>
												<td className="px-6 py-6 align-top text-sm text-gray-900">
													{hasWriteSettingsPermission ? (
														<select
															value={getTypeFromDict(chair.type)}
															onChange={e => {
																const updatedChairs = [...chairs]
																const newType = {
																	Chair: false,
																	Bed: false,
																	Gurney: false,
																	[e.target.value]: true
																}
																updatedChairs[originalIndex].type =
																	newType
																setChairs(updatedChairs)
																setChairsModified(
																	hasChairsDataChanged()
																)
															}}
															className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
														>
															<option value="Chair">Chair</option>
															<option value="Bed">Bed</option>
															<option value="Gurney">Gurney</option>
														</select>
													) : (
														<span>{getTypeFromDict(chair.type)}</span>
													)}
												</td>
												<td className="px-6 py-6 align-top text-sm text-gray-900">
													{hasWriteSettingsPermission ? (
														<select
															value={getIsolationFromDict(
																chair.isolation
															)}
															onChange={e => {
																const updatedChairs = [...chairs]
																const newIsolation = {
																	"Public room": false,
																	"Private room with bathroom": false,
																	"Private room without bathroom": false,
																	[e.target.value]: true
																}
																updatedChairs[
																	originalIndex
																].isolation = newIsolation
																setChairs(updatedChairs)
																setChairsModified(
																	hasChairsDataChanged()
																)
															}}
															className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
														>
															<option value="Public room">
																Public room
															</option>
															<option value="Private room with bathroom">
																Private room with bathroom
															</option>
															<option value="Private room without bathroom">
																Private room without bathroom
															</option>
														</select>
													) : (
														<span>
															{getIsolationFromDict(chair.isolation)}
														</span>
													)}
												</td>
												<td className="px-6 py-6 align-top text-sm text-gray-900">
													{hasWriteSettingsPermission ? (
														<select
															value={chair.status ? "true" : "false"}
															onChange={e => {
																const updatedChairs = [...chairs]
																const newStatus =
																	e.target.value === "true"
																updatedChairs[
																	originalIndex
																].status = newStatus
																setChairs(updatedChairs)
																setChairsModified(
																	hasChairsDataChanged()
																)
															}}
															className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
														>
															<option value="true">Active</option>
															<option value="false">Inactive</option>
														</select>
													) : (
														<span>
															{chair.status ? "Active" : "Inactive"}
														</span>
													)}
												</td>
												{hasWriteSettingsPermission && (
													<td className="px-6 py-6 align-top text-sm text-gray-900">
														<button
															type="button"
															onClick={() =>
																handleChairDelete(originalIndex)
															}
															className="py-2 px-4 w-24 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-2"
														>
															<BsTrash3Fill className="h-4 w-4" />
															Delete
														</button>
													</td>
												)}
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	)
}

export default ChairTable
